import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './baseQuery';
import type {
  TargetTotalRow, SourceTableRow, StatusDescRow, StatusRow, SubstitutionRow,
  ApprovalCheckRow, PerTargetTotalsResponse, Unit,
} from '../types/api/generated';
import {
  TargetTotalRowSchema, SourceTableRowSchema, StatusDescRowSchema, StatusRowSchema,
  SubstitutionRowSchema, ApprovalCheckRowSchema, PerTargetTotalsResponseSchema,
} from '../types/api/schemas';
import { validateResponse } from './validateResponse';
import { z } from 'zod';

export const targetingApi = createApi({
    reducerPath: 'targetingApi',
    baseQuery,
    endpoints: (builder) => ({
        getCurrentTotals: builder.mutation<TargetTotalRow[], unknown>({
            query: (body) => ({ url: 'api/v2/targeting/get-current-totals', method: 'POST', body }),
            transformResponse: validateResponse(TargetTotalRowSchema.array()),
        }),
        getPerTargetTotals: builder.mutation<PerTargetTotalsResponse, unknown>({
            query: (body) => ({ url: 'api/v2/targeting/get-per-target-totals', method: 'POST', body }),
            transformResponse: validateResponse(PerTargetTotalsResponseSchema),
        }),
        getSourceTableData: builder.mutation<SourceTableRow[], unknown>({
            query: (body) => ({ url: 'api/v2/targeting/get-source-table-data', method: 'POST', body }),
            transformResponse: validateResponse(SourceTableRowSchema.array()),
        }),
        getReasons: builder.mutation<StatusDescRow[], unknown>({
            query: (body) => ({ url: 'api/v2/admin/exerciseadmin/get-reasons', method: 'POST', body }),
            transformResponse: validateResponse(StatusDescRowSchema.array()),
        }),
        revertTargetType: builder.mutation<Unit, unknown>({
            query: (body) => ({ url: 'api/v2/targeting/revert-target-type', method: 'POST', body }),
        }),
        setTargetType: builder.mutation<Unit, unknown>({
            query: (body) => ({ url: 'api/v2/targeting/set-target-type', method: 'POST', body }),
        }),
        submitStatus: builder.mutation<Unit, unknown>({
            query: (body) => ({ url: 'api/v2/targeting/submit-status', method: 'POST', body }),
        }),
        approvalCheck: builder.mutation<ApprovalCheckRow[], unknown>({
            query: (body) => ({ url: 'api/v2/targeting/approval-check', method: 'POST', body }),
            transformResponse: validateResponse(ApprovalCheckRowSchema.array()),
        }),
        getApprovedExercises: builder.mutation<Unit, unknown>({
            query: (body) => ({ url: 'api/v2/targeting/get-approved-exercises', method: 'POST', body }),
        }),
        getStatusById: builder.mutation<StatusRow[], unknown>({
            query: (body) => ({ url: 'api/v2/targeting/get-status-by-id', method: 'POST', body }),
            transformResponse: validateResponse(StatusRowSchema.array()),
        }),
        getMySubstitutions: builder.mutation<SubstitutionRow[], unknown>({
            query: (body) => ({ url: 'api/v2/targeting/get-my-substitutions', method: 'POST', body }),
            transformResponse: validateResponse(SubstitutionRowSchema.array()),
        }),
    }),
});

export const {
    useGetCurrentTotalsMutation,
    useGetPerTargetTotalsMutation,
    useGetSourceTableDataMutation,
    useGetReasonsMutation,
    useRevertTargetTypeMutation,
    useSetTargetTypeMutation,
    useSubmitStatusMutation,
    useApprovalCheckMutation,
    useGetApprovedExercisesMutation,
    useGetStatusByIdMutation,
    useGetMySubstitutionsMutation,
} = targetingApi;
